import axios from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';
import { installInterceptors, renewAccessToken } from './interceptors';
import { refreshAuthTokens } from './authRefresh';
import { navigateTo } from '@shared/lib/navigation';

/**
 * Tenant (regional) API client. Base URL is dynamic — the regional edge
 * is determined by Core `/resolve-domain` and stored in authStore. We rebind
 * the baseURL on every request via the request interceptor below so that
 * tenant switches take effect immediately without rebuilding the instance.
 */
export const tenantApi = axios.create({
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

tenantApi.interceptors.request.use((config) => {
  const regional = useAuthStore.getState().regionalApiUrl;
  config.baseURL = regional || env.apiBaseUrl;
  return config;
});

const sessionRefresh = {
  refresh: refreshAuthTokens,
  // Navigate only once the session is cleared: before the router mounts this is a full page load,
  // and one that cut sign-out short would boot on the same refresh token and fail again, forever.
  onRefreshFailed: () => {
    void useAuthStore.getState().signOut().finally(() => navigateTo('/login', { replace: true }));
  },
};

installInterceptors(tenantApi, sessionRefresh);

/** Renew the session's access token now (cold start, biometric unlock), sharing any renewal in flight. */
export const renewSession = (): Promise<string | null> => renewAccessToken(sessionRefresh);

/**
 * Signup is addressed to the community being joined, not the tenant the app happens to be
 * resolved to. `resolveDomain()` rewrites the ambient tenant id — including on a hub
 * re-resolution — so a signup that trusted it could ask the hub whether a community's member
 * type exists, and be told it does not.
 */
export function signupTenantHeaders(tenantId?: string) {
  const id = (tenantId ?? '').trim();
  return id ? { headers: { 'x-tenant-id': id } } : undefined;
}
